export type PreOpBoardStatusField =
  | 'preOpBoardAnesthesiaReady'
  | 'preOpBoardSurgeonReady'
  | 'preOpBoardHistoryAndPhysicalReady';

export type PreOpBoardPatient = {
  id: string | number;
  name: string;
  bed?: string | null;
  priority?: string | null;
  physician?: { id?: string | number; name?: string | null } | null;
  preOpBoardAnesthesiologist?: { id?: string | number; name?: string | null } | null;
  procedure?: {
    id?: string | number;
    startTime?: string | null;
    startTimeText?: string | null;
    anesthesiaType?: string | null;
  } | null;
  preOpNurse?: { id?: string | number; name?: string | null } | null;
  events?: { readyForOrAt?: string | null } | null;
  preOpBoardAnesthesiaReady?: boolean | null;
  preOpBoardSurgeonReady?: boolean | null;
  preOpBoardHistoryAndPhysicalReady?: boolean | null;
};

export type PreOpBoardSort = {
  field: 'caseOrder' | 'time';
  direction: 'asc' | 'desc';
};

export type PreOpBoardGroup = {
  key: string;
  name: string;
  patients: PreOpBoardPatient[];
};

const normalizedText = (value?: string | null) => (value || '').trim().toLocaleLowerCase();

export type PreOpBoardMdaOption = { id: string; name: string };

const mdaId = (patient: PreOpBoardPatient) =>
  patient.preOpBoardAnesthesiologist?.id == null ? 'unassigned' : String(patient.preOpBoardAnesthesiologist.id);

export const preOpBoardMdaOptions = (patients: PreOpBoardPatient[]): PreOpBoardMdaOption[] => {
  const options = new Map<string, PreOpBoardMdaOption>();
  patients.forEach(patient => {
    const id = mdaId(patient);
    options.set(id, {
      id,
      name: id === 'unassigned' ? 'Unassigned' : patient.preOpBoardAnesthesiologist?.name?.trim() || `MDA ${id}`,
    });
  });
  return [...options.values()].sort((left, right) => {
    if (left.id === 'unassigned') return 1;
    if (right.id === 'unassigned') return -1;
    return (
      normalizedText(left.name).localeCompare(normalizedText(right.name)) ||
      left.id.localeCompare(right.id, undefined, { numeric: true })
    );
  });
};

export const filterPreOpBoardPatients = (patients: PreOpBoardPatient[], selectedMdaId: string | null) =>
  selectedMdaId === null ? patients : patients.filter(patient => mdaId(patient) === selectedMdaId);

const timestamp = (patient: PreOpBoardPatient) => {
  const value = patient.procedure?.startTime;
  if (!value) return null;
  const result = new Date(value).getTime();
  return Number.isNaN(result) ? null : result;
};

const priority = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return { category: 2, numeric: null, text: '' };
  if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
    return { category: 0, numeric: Number(trimmed), text: trimmed };
  }
  return { category: 1, numeric: null, text: normalizedText(trimmed) };
};

const compareOptionalNumber = (left: number | null, right: number | null, direction: 'asc' | 'desc') => {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return (left - right) * (direction === 'asc' ? 1 : -1);
};

const compareCaseOrder = (left: PreOpBoardPatient, right: PreOpBoardPatient, direction: 'asc' | 'desc') => {
  const leftPriority = priority(left.priority);
  const rightPriority = priority(right.priority);
  if (leftPriority.category !== rightPriority.category) return leftPriority.category - rightPriority.category;

  if (leftPriority.category === 0) {
    const numeric = ((leftPriority.numeric || 0) - (rightPriority.numeric || 0)) * (direction === 'asc' ? 1 : -1);
    if (numeric !== 0) return numeric;
  }

  if (leftPriority.category === 1) {
    const text = leftPriority.text.localeCompare(rightPriority.text) * (direction === 'asc' ? 1 : -1);
    if (text !== 0) return text;
  }

  return compareOptionalNumber(timestamp(left), timestamp(right), 'asc');
};

const comparePatients = (sort: PreOpBoardSort) => (left: PreOpBoardPatient, right: PreOpBoardPatient) => {
  const primary =
    sort.field === 'time'
      ? compareOptionalNumber(timestamp(left), timestamp(right), sort.direction)
      : compareCaseOrder(left, right, sort.direction);
  if (primary !== 0) return primary;

  const secondary = sort.field === 'time' ? compareCaseOrder(left, right, 'asc') : 0;
  if (secondary !== 0) return secondary;
  return String(left.id).localeCompare(String(right.id), undefined, { numeric: true });
};

export const groupPreOpBoardPatients = (
  patients: PreOpBoardPatient[],
  sort: PreOpBoardSort = { field: 'caseOrder', direction: 'asc' }
): PreOpBoardGroup[] => {
  const groups = patients.reduce<Map<string, { name: string; patients: PreOpBoardPatient[] }>>((result, patient) => {
    const physicianName = patient.physician?.name?.trim() || 'Unassigned';
    const physicianKey = patient.physician?.id == null ? 'unassigned' : String(patient.physician.id);
    const group = result.get(physicianKey) || { name: physicianName, patients: [] };
    group.patients.push(patient);
    result.set(physicianKey, group);
    return result;
  }, new Map());

  return Array.from(groups.entries())
    .sort(([leftKey, left], [rightKey, right]) => {
      if (leftKey === 'unassigned') return 1;
      if (rightKey === 'unassigned') return -1;
      const byName = normalizedText(left.name).localeCompare(normalizedText(right.name));
      return byName || leftKey.localeCompare(rightKey, undefined, { numeric: true });
    })
    .map(([key, group]) => ({
      key,
      name: group.name,
      patients: [...group.patients].sort(comparePatients(sort)),
    }));
};

export const isReadyForOr = (patient: PreOpBoardPatient) => Boolean(patient.events?.readyForOrAt);
